import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, 
  Filter, 
  Heart, 
  Star, 
  Zap, 
  Leaf, 
  Droplets, 
  Flame,
  Shield,
  Users,
  Clock,
  TrendingUp,
  Scale,
  Eye,
  ChevronDown,
  ChevronUp,
  X,
  Info,
  Calculator,
  BookOpen,
  Settings,
  Users2,
  Package,
  Gift,
  AlertTriangle
} from 'lucide-react'
import { useApi } from '../contexts/ApiContext'
import { Pet, PetRarity, PetAbilityCategory, PetSearchFilters } from '../types/api'
import './PetWiki.css'

// Static pet data based on the wiki
const staticPetData: Pet[] = [
  // Common Pets
  {
    id: 'starfish',
    name: 'Starfish',
    rarity: 'common',
    obtainable: true,
    icon: '/pets/starfish.png',
    description: 'A passive pet that gains XP automatically',
    abilities: [
      {
        title: 'Passive XP',
        description: 'Gains XP automatically without feeding',
        category: 'experience_boosters',
        pets: ['Starfish']
      }
    ],
    base_weight: 1,
    hunger_rate: 0,
    xp_rate: 1,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'crab',
    name: 'Crab',
    rarity: 'common',
    obtainable: true,
    icon: '/pets/crab.png',
    description: 'A mischievous pet that can pinch other players',
    abilities: [
      {
        title: 'Pinch Pocket',
        description: 'Pinches another player and grants the player some sheckles',
        category: 'player_interaction',
        pets: ['Crab']
      }
    ],
    base_weight: 1.5,
    hunger_rate: 1,
    xp_rate: 1,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'seagull',
    name: 'Seagull',
    rarity: 'common',
    obtainable: true,
    icon: '/pets/seagull.png',
    description: 'A coastal bird pet',
    abilities: [
      {
        title: 'Coastal Friend',
        description: 'Boosts nearby coastal fruits',
        category: 'growth_harvest',
        pets: ['Seagull']
      }
    ],
    base_weight: 2,
    hunger_rate: 1,
    xp_rate: 1,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'bunny',
    name: 'Bunny',
    rarity: 'common',
    obtainable: true,
    icon: '/pets/bunny.png',
    description: 'A cute rabbit that loves carrots',
    abilities: [
      {
        title: 'Carrot Chomper',
        description: 'Eats carrots and grants bonus sheckles',
        category: 'resource_gatherers',
        pets: ['Bunny']
      }
    ],
    base_weight: 1.2,
    hunger_rate: 1,
    xp_rate: 1,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'dog',
    name: 'Dog',
    rarity: 'common',
    obtainable: true,
    icon: '/pets/dog.png',
    description: 'Man\'s best friend in the garden',
    abilities: [
      {
        title: 'Treasure Hunter',
        description: 'Occasionally digs up a random seed at a 5% chance',
        category: 'resource_gatherers',
        pets: ['Dog']
      }
    ],
    base_weight: 3,
    hunger_rate: 1,
    xp_rate: 1,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'golden_lab',
    name: 'Golden Lab',
    rarity: 'common',
    obtainable: true,
    icon: '/pets/golden_lab.png',
    description: 'A golden retriever with enhanced digging abilities',
    abilities: [
      {
        title: 'Digging Friend',
        description: 'Occasionally digs up a random seed at a 10% chance',
        category: 'resource_gatherers',
        pets: ['Golden Lab']
      }
    ],
    base_weight: 4,
    hunger_rate: 1,
    xp_rate: 1,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Uncommon Pets
  {
    id: 'bee',
    name: 'Bee',
    rarity: 'uncommon',
    obtainable: true,
    icon: '/pets/bee.png',
    description: 'A buzzing bee that pollinates flowers',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Bee']
      }
    ],
    base_weight: 0.5,
    hunger_rate: 1,
    xp_rate: 1.2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'shiba_inu',
    name: 'Shiba Inu',
    rarity: 'uncommon',
    obtainable: true,
    icon: '/pets/shiba_inu.png',
    description: 'A loyal Japanese dog breed',
    abilities: [
      {
        title: 'Man\'s Best Tomodachi',
        description: 'Occasionally digs up a random seed at a 15% chance',
        category: 'resource_gatherers',
        pets: ['Shiba Inu']
      }
    ],
    base_weight: 2.5,
    hunger_rate: 1,
    xp_rate: 1.2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'maneki_neko',
    name: 'Maneki-neko',
    rarity: 'uncommon',
    obtainable: true,
    icon: '/pets/maneki_neko.png',
    description: 'A lucky cat that brings fortune',
    abilities: [
      {
        title: 'Lucky Charm',
        description: 'Increases lucky harvest chance',
        category: 'growth_harvest',
        pets: ['Maneki-neko']
      }
    ],
    base_weight: 1.8,
    hunger_rate: 1,
    xp_rate: 1.2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Rare Pets
  {
    id: 'flamingo',
    name: 'Flamingo',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/flamingo.png',
    description: 'A pink flamingo that loves tropical environments',
    abilities: [
      {
        title: 'Tropical Grace',
        description: 'Boosts growth of tropical fruits',
        category: 'growth_harvest',
        pets: ['Flamingo']
      }
    ],
    base_weight: 3.5,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'toucan',
    name: 'Toucan',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/toucan.png',
    description: 'A colorful bird with a large beak',
    abilities: [
      {
        title: 'Tropical Lover',
        description: 'Boosts size and variant chance of nearby tropical fruits',
        category: 'growth_harvest',
        pets: ['Toucan']
      }
    ],
    base_weight: 2.8,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'sea_turtle',
    name: 'Sea Turtle',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/sea_turtle.png',
    description: 'A slow but steady sea creature',
    abilities: [
      {
        title: 'Wet',
        description: 'Grants Wet mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Sea Turtle']
      },
      {
        title: 'Shell Share',
        description: 'Occasionally grants bonus experience to a random active pet',
        category: 'experience_boosters',
        pets: ['Sea Turtle']
      }
    ],
    base_weight: 5,
    hunger_rate: 0.8,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'orangutan',
    name: 'Orangutan',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/orangutan.png',
    description: 'A clever ape that helps with crafting',
    abilities: [
      {
        title: 'Helping Hands',
        description: 'Each material used in crafting has a chance not to be consumed',
        category: 'resource_gatherers',
        pets: ['Orangutan']
      }
    ],
    base_weight: 8,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'seal',
    name: 'Seal',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/seal.png',
    description: 'A playful seal from the ocean',
    abilities: [
      {
        title: 'Seal the Deal',
        description: 'Chance to receive the pet back as an egg when sold',
        category: 'resource_gatherers',
        pets: ['Seal']
      }
    ],
    base_weight: 6,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'honey_bee',
    name: 'Honey Bee',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/honey_bee.png',
    description: 'A bee that produces honey',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Honey Bee']
      }
    ],
    base_weight: 0.6,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'wasp',
    name: 'Wasp',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/wasp.png',
    description: 'An aggressive wasp with a powerful sting',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Wasp']
      },
      {
        title: 'Stinger',
        description: 'Advances cooldown of a random pet by 60s',
        category: 'special_abilities',
        pets: ['Wasp']
      }
    ],
    base_weight: 0.4,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'nihonzaru',
    name: 'Nihonzaru',
    rarity: 'rare',
    obtainable: true,
    icon: '/pets/nihonzaru.png',
    description: 'A Japanese macaque with mystical powers',
    abilities: [
      {
        title: 'Mystic Aura',
        description: 'Boosts all nearby pet abilities',
        category: 'pet_interactions',
        pets: ['Nihonzaru']
      }
    ],
    base_weight: 7,
    hunger_rate: 1,
    xp_rate: 1.5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Legendary Pets
  {
    id: 'grey_mouse',
    name: 'Grey Mouse',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/grey_mouse.png',
    description: 'A small but mighty mouse',
    abilities: [
      {
        title: 'Tiny Helper',
        description: 'Increases harvest efficiency for small fruits',
        category: 'growth_harvest',
        pets: ['Grey Mouse']
      }
    ],
    base_weight: 0.3,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'tarantula_hawk',
    name: 'Tarantula Hawk',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/tarantula_hawk.png',
    description: 'A fearsome wasp that hunts tarantulas',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Tarantula Hawk']
      },
      {
        title: 'Stinger',
        description: 'Advances cooldown of a random pet by 60s',
        category: 'special_abilities',
        pets: ['Tarantula Hawk']
      }
    ],
    base_weight: 1.2,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'kodama',
    name: 'Kodama',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/kodama.png',
    description: 'A forest spirit that protects trees',
    abilities: [
      {
        title: 'Forest Guardian',
        description: 'Boosts growth of all plants in the garden',
        category: 'growth_harvest',
        pets: ['Kodama']
      }
    ],
    base_weight: 1,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'corrupted_kodama',
    name: 'Corrupted Kodama',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/corrupted_kodama.png',
    description: 'A corrupted forest spirit with dark powers',
    abilities: [
      {
        title: 'Dark Aura',
        description: 'Corrupts nearby fruits with dark mutations',
        category: 'mutation_effects',
        pets: ['Corrupted Kodama']
      }
    ],
    base_weight: 1.5,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'caterpillar',
    name: 'Caterpillar',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/caterpillar.png',
    description: 'A leaf-eating caterpillar that will become a butterfly',
    abilities: [
      {
        title: 'Leaf Lover Passive',
        description: 'Boosts growth rate of leafy plants nearby',
        category: 'growth_harvest',
        pets: ['Caterpillar']
      }
    ],
    base_weight: 0.2,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'snail',
    name: 'Snail',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/snail.png',
    description: 'A slow but steady snail',
    abilities: [
      {
        title: 'Slow and Steady',
        description: 'Increases lucky harvest chance',
        category: 'growth_harvest',
        pets: ['Snail']
      }
    ],
    base_weight: 0.1,
    hunger_rate: 0.5,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'petal_bee',
    name: 'Petal Bee',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/petal_bee.png',
    description: 'A bee made of flower petals',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Petal Bee']
      }
    ],
    base_weight: 0.3,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'moth',
    name: 'Moth',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/moth.png',
    description: 'A nocturnal moth attracted to light',
    abilities: [
      {
        title: 'Silksong',
        description: 'Restores hunger of a random pet',
        category: 'special_abilities',
        pets: ['Moth']
      }
    ],
    base_weight: 0.4,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'scarlet_macaw',
    name: 'Scarlet Macaw',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/scarlet_macaw.png',
    description: 'A colorful parrot with vibrant feathers',
    abilities: [
      {
        title: 'Verdant',
        description: 'Grants Verdant mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Scarlet Macaw']
      }
    ],
    base_weight: 2.5,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'ostrich',
    name: 'Ostrich',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/ostrich.png',
    description: 'A large flightless bird that runs fast',
    abilities: [
      {
        title: 'Eggsperience',
        description: 'Pets hatched from eggs start with increased age',
        category: 'egg_hatch',
        pets: ['Ostrich']
      }
    ],
    base_weight: 15,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'peacock',
    name: 'Peacock',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/peacock.png',
    description: 'A beautiful bird with colorful tail feathers',
    abilities: [
      {
        title: 'Colorful Display',
        description: 'Boosts the visual appeal of nearby fruits',
        category: 'growth_harvest',
        pets: ['Peacock']
      }
    ],
    base_weight: 4,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'capybara',
    name: 'Capybara',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/capybara.png',
    description: 'The world\'s largest rodent, very friendly',
    abilities: [
      {
        title: 'Chill Zone',
        description: 'Prevents hunger loss and grants experience every second nearby',
        category: 'experience_boosters',
        pets: ['Capybara']
      }
    ],
    base_weight: 12,
    hunger_rate: 0.8,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'tanuki',
    name: 'Tanuki',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/tanuki.png',
    description: 'A Japanese raccoon dog with magical powers',
    abilities: [
      {
        title: 'Mischief',
        description: 'Every ~10 minutes, causes mischief, doing random different actions in the player\'s garden',
        category: 'special_abilities',
        pets: ['Tanuki']
      }
    ],
    base_weight: 3,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'tanchozuru',
    name: 'Tanchozuru',
    rarity: 'legendary',
    obtainable: true,
    icon: '/pets/tanchozuru.png',
    description: 'A Japanese crane symbolizing good fortune',
    abilities: [
      {
        title: 'Good Fortune',
        description: 'Increases luck in all activities',
        category: 'growth_harvest',
        pets: ['Tanchozuru']
      }
    ],
    base_weight: 2,
    hunger_rate: 1.2,
    xp_rate: 2,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Mythical Pets
  {
    id: 'brown_mouse',
    name: 'Brown Mouse',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/brown_mouse.png',
    description: 'A rare brown mouse with enhanced abilities',
    abilities: [
      {
        title: 'Enhanced Helper',
        description: 'Significantly increases harvest efficiency for all fruits',
        category: 'growth_harvest',
        pets: ['Brown Mouse']
      }
    ],
    base_weight: 0.4,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'giant_ant',
    name: 'Giant Ant',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/giant_ant.png',
    description: 'A massive ant with incredible strength',
    abilities: [
      {
        title: 'For the Blue Colony',
        description: 'Chance to duplicate harvested plants or candy',
        category: 'resource_gatherers',
        pets: ['Giant Ant']
      },
      {
        title: 'Harvester',
        description: 'Increased chance to duplicate candy-type plants',
        category: 'resource_gatherers',
        pets: ['Giant Ant']
      }
    ],
    base_weight: 2,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'praying_mantis',
    name: 'Praying Mantis',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/praying_mantis.png',
    description: 'A praying mantis with zen-like powers',
    abilities: [
      {
        title: 'Zen zone',
        description: 'Boosts variant chance nearby while praying',
        category: 'growth_harvest',
        pets: ['Praying Mantis']
      }
    ],
    base_weight: 0.8,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'red_giant_ant',
    name: 'Red Giant Ant',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/red_giant_ant.png',
    description: 'A red giant ant with fiery powers',
    abilities: [
      {
        title: 'Fiery Harvest',
        description: 'Chance to burn nearby fruits for special effects',
        category: 'mutation_effects',
        pets: ['Red Giant Ant']
      }
    ],
    base_weight: 2.5,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'squirrel',
    name: 'Squirrel',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/squirrel.png',
    description: 'A quick squirrel that stores nuts',
    abilities: [
      {
        title: 'Seed Stash',
        description: 'Chance to not consume a use when using the Reclaimer',
        category: 'resource_gatherers',
        pets: ['Squirrel']
      }
    ],
    base_weight: 1.5,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'bear_bee',
    name: 'Bear Bee',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/bear_bee.png',
    description: 'A bee with the strength of a bear',
    abilities: [
      {
        title: 'HoneyGlazed',
        description: 'Grants HoneyGlazed mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Bear Bee']
      }
    ],
    base_weight: 3,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'butterfly',
    name: 'Butterfly',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/butterfly.png',
    description: 'A beautiful butterfly with rainbow wings',
    abilities: [
      {
        title: 'Rainbow',
        description: 'Grants Rainbow mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Butterfly']
      }
    ],
    base_weight: 0.3,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'pack_bee',
    name: 'Pack Bee',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/pack_bee.png',
    description: 'A bee that works in a pack',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Pack Bee']
      }
    ],
    base_weight: 0.7,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'mimic_octopus',
    name: 'Mimic Octopus',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/mimic_octopus.png',
    description: 'An octopus that can mimic other creatures',
    abilities: [
      {
        title: 'Mimicry',
        description: 'Copies the ability of another pet',
        category: 'pet_interactions',
        pets: ['Mimic Octopus']
      }
    ],
    base_weight: 4,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'kappa',
    name: 'Kappa',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/kappa.png',
    description: 'A Japanese water spirit',
    abilities: [
      {
        title: 'Wet, Bloodlit',
        description: 'Grants Wet and Bloodlit mutations to nearby fruits',
        category: 'mutation_effects',
        pets: ['Kappa']
      }
    ],
    base_weight: 3.5,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'koi',
    name: 'Koi',
    rarity: 'mythical',
    obtainable: true,
    icon: '/pets/koi.png',
    description: 'A colorful koi fish symbolizing good luck',
    abilities: [
      {
        title: 'Fish of Fortune',
        description: 'Grants a chance to recover an egg when hatching!',
        category: 'egg_hatch',
        pets: ['Koi']
      }
    ],
    base_weight: 2,
    hunger_rate: 1.5,
    xp_rate: 3,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'raiju',
    name: 'Raiju',
    rarity: 'divine',
    obtainable: true,
    icon: '/pets/raiju.png',
    description: 'A thunder beast from Japanese mythology',
    abilities: [
      {
        title: 'Devour and Spread',
        description: 'Eats Shocked fruit, then mutates multiple fruits with Static or Shocked',
        category: 'mutation_effects',
        pets: ['Raiju']
      }
    ],
    base_weight: 5,
    hunger_rate: 2,
    xp_rate: 5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Divine Pets
  {
    id: 'red_fox',
    name: 'Red Fox',
    rarity: 'divine',
    obtainable: true,
    icon: '/pets/red_fox.png',
    description: 'A cunning fox with magical abilities',
    abilities: [
      {
        title: 'Scoundrel',
        description: 'Attempts to steal a seed from another player\'s plant',
        category: 'player_interaction',
        pets: ['Red Fox']
      }
    ],
    base_weight: 3,
    hunger_rate: 2,
    xp_rate: 5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'dragonfly',
    name: 'Dragonfly',
    rarity: 'divine',
    obtainable: true,
    icon: '/pets/dragonfly.png',
    description: 'A swift dragonfly with golden powers',
    abilities: [
      {
        title: 'Gold',
        description: 'Grants Gold mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Dragonfly']
      }
    ],
    base_weight: 0.8,
    hunger_rate: 2,
    xp_rate: 5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'disco_bee',
    name: 'Disco Bee',
    rarity: 'divine',
    obtainable: true,
    icon: '/pets/disco_bee.png',
    description: 'A bee that loves to dance',
    abilities: [
      {
        title: 'Disco',
        description: 'Grants Disco mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Disco Bee']
      }
    ],
    base_weight: 1,
    hunger_rate: 2,
    xp_rate: 5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  {
    id: 'queen_bee',
    name: 'Queen Bee',
    rarity: 'divine',
    obtainable: true,
    icon: '/pets/queen_bee.png',
    description: 'The queen of all bees',
    abilities: [
      {
        title: 'Pollinated',
        description: 'Grants Pollinated mutation to nearby fruits',
        category: 'mutation_effects',
        pets: ['Queen Bee']
      },
      {
        title: 'For the Queen',
        description: 'Refreshes the pet with the highest cooldown',
        category: 'special_abilities',
        pets: ['Queen Bee']
      }
    ],
    base_weight: 2,
    hunger_rate: 2,
    xp_rate: 5,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Prismatic Pets
  {
    id: 'french_fry_ferret',
    name: 'French Fry Ferret',
    rarity: 'prismatic',
    obtainable: true,
    icon: '/pets/french_fry_ferret.png',
    description: 'A rare ferret that loves french fries',
    abilities: [
      {
        title: 'Friendly Frier',
        description: 'Increases a random pet\'s level by 1',
        category: 'pet_interactions',
        pets: ['French Fry Ferret']
      }
    ],
    base_weight: 1.5,
    hunger_rate: 3,
    xp_rate: 10,
    acquisition_method: 'Obtainable through normal gameplay'
  },
  // Unobtainable Pets (Limited Time/Event Exclusive)
  {
    id: 'red_dragon',
    name: 'Red Dragon',
    rarity: 'common',
    obtainable: false,
    icon: '/pets/red_dragon.png',
    description: 'A mythical red dragon from limited events',
    abilities: [
      {
        title: 'Dragon Breath',
        description: 'Occasionally breathes fire to advance plant growth',
        category: 'growth_harvest',
        pets: ['Red Dragon']
      }
    ],
    base_weight: 15,
    hunger_rate: 5,
    xp_rate: 8,
    acquisition_method: 'Limited event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'cat',
    name: 'Cat',
    rarity: 'uncommon',
    obtainable: false,
    icon: '/pets/cat.png',
    description: 'A domestic cat that boosts fruit size while napping',
    abilities: [
      {
        title: 'Cat Nap',
        description: 'Boosts nearby fruit size while napping randomly on the player\'s farm',
        category: 'growth_harvest',
        pets: ['Cat']
      }
    ],
    base_weight: 4.5,
    hunger_rate: 2,
    xp_rate: 2.5,
    acquisition_method: 'Limited event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 't_rex',
    name: 'T-Rex',
    rarity: 'divine',
    obtainable: false,
    icon: '/pets/t_rex.png',
    description: 'A fearsome Tyrannosaurus Rex from prehistoric events',
    abilities: [
      {
        title: 'Mutation Transfer',
        description: 'Removes a random mutation from three fruits and applies them to one other fruit',
        category: 'mutation_effects',
        pets: ['T-Rex']
      }
    ],
    base_weight: 25,
    hunger_rate: 8,
    xp_rate: 10,
    acquisition_method: 'Prehistoric event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'brontosaurus',
    name: 'Brontosaurus',
    rarity: 'mythical',
    obtainable: false,
    icon: '/pets/brontosaurus.png',
    description: 'A massive long-necked dinosaur',
    abilities: [
      {
        title: 'Giant Incubator',
        description: 'Pets hatched from eggs have an increase in base size and weight',
        category: 'egg_hatch',
        pets: ['Brontosaurus']
      }
    ],
    base_weight: 50,
    hunger_rate: 10,
    xp_rate: 8,
    acquisition_method: 'Prehistoric event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'triceratops',
    name: 'Triceratops',
    rarity: 'legendary',
    obtainable: false,
    icon: '/pets/triceratops.png',
    description: 'A three-horned dinosaur with constant stats',
    abilities: [
      {
        title: 'Tri-Horn',
        description: 'Charges into 3 plants, advancing their growth',
        category: 'growth_harvest',
        pets: ['Triceratops']
      }
    ],
    base_weight: 20,
    hunger_rate: 5,
    xp_rate: 5,
    acquisition_method: 'Prehistoric event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'kitsune',
    name: 'Kitsune',
    rarity: 'prismatic',
    obtainable: false,
    icon: '/pets/kitsune.png',
    description: 'A nine-tailed fox with magical powers',
    abilities: [
      {
        title: 'Nine-Tailed Myth',
        description: 'Every ~22 Minutes, goes to another player\'s fruit, mutates it with Chakra and duplicates and gives it to the player',
        category: 'player_interaction',
        pets: ['Kitsune']
      }
    ],
    base_weight: 3,
    hunger_rate: 4,
    xp_rate: 15,
    acquisition_method: 'Limited event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'santa_claus',
    name: 'Santa Claus',
    rarity: 'divine',
    obtainable: false,
    icon: '/pets/santa_claus.png',
    description: 'A jolly gift-giving figure from Christmas events',
    abilities: [
      {
        title: 'Gift Giving',
        description: 'Occasionally gives random items to the player',
        category: 'player_interaction',
        pets: ['Santa Claus']
      }
    ],
    base_weight: 12,
    hunger_rate: 3,
    xp_rate: 6,
    acquisition_method: 'Christmas event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'reindeer',
    name: 'Reindeer',
    rarity: 'legendary',
    obtainable: false,
    icon: '/pets/reindeer.png',
    description: 'A magical reindeer that can fly',
    abilities: [
      {
        title: 'Flight',
        description: 'Can fly over obstacles and reach distant plants',
        category: 'special_abilities',
        pets: ['Reindeer']
      }
    ],
    base_weight: 8,
    hunger_rate: 2,
    xp_rate: 4,
    acquisition_method: 'Christmas event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'easter_bunny',
    name: 'Easter Bunny',
    rarity: 'mythical',
    obtainable: false,
    icon: '/pets/easter_bunny.png',
    description: 'A magical bunny that hides eggs',
    abilities: [
      {
        title: 'Egg Hunt',
        description: 'Occasionally finds hidden eggs with rare items',
        category: 'resource_gatherers',
        pets: ['Easter Bunny']
      }
    ],
    base_weight: 6,
    hunger_rate: 2,
    xp_rate: 5,
    acquisition_method: 'Easter event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'pumpkin_king',
    name: 'Pumpkin King',
    rarity: 'divine',
    obtainable: false,
    icon: '/pets/pumpkin_king.png',
    description: 'A spooky king of pumpkins from Halloween events',
    abilities: [
      {
        title: 'Pumpkin Power',
        description: 'Boosts pumpkin growth and grants special mutations',
        category: 'growth_harvest',
        pets: ['Pumpkin King']
      }
    ],
    base_weight: 18,
    hunger_rate: 4,
    xp_rate: 7,
    acquisition_method: 'Halloween event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'ghost',
    name: 'Ghost',
    rarity: 'rare',
    obtainable: false,
    icon: '/pets/ghost.png',
    description: 'A spooky ghost that haunts the farm',
    abilities: [
      {
        title: 'Haunting',
        description: 'Occasionally scares other pets, making them work harder',
        category: 'pet_interactions',
        pets: ['Ghost']
      }
    ],
    base_weight: 0.1,
    hunger_rate: 1,
    xp_rate: 3,
    acquisition_method: 'Halloween event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'valentine_angel',
    name: 'Valentine Angel',
    rarity: 'mythical',
    obtainable: false,
    icon: '/pets/valentine_angel.png',
    description: 'A loving angel that spreads romance',
    abilities: [
      {
        title: 'Love Aura',
        description: 'Increases pet happiness and reduces hunger rate',
        category: 'pet_interactions',
        pets: ['Valentine Angel']
      }
    ],
    base_weight: 5,
    hunger_rate: 1,
    xp_rate: 6,
    acquisition_method: 'Valentine\'s event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'cupid',
    name: 'Cupid',
    rarity: 'legendary',
    obtainable: false,
    icon: '/pets/cupid.png',
    description: 'A winged archer of love',
    abilities: [
      {
        title: 'Love Arrow',
        description: 'Occasionally makes two pets fall in love, boosting their abilities',
        category: 'pet_interactions',
        pets: ['Cupid']
      }
    ],
    base_weight: 3,
    hunger_rate: 2,
    xp_rate: 4,
    acquisition_method: 'Valentine\'s event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'fireworks_dragon',
    name: 'Fireworks Dragon',
    rarity: 'prismatic',
    obtainable: false,
    icon: '/pets/fireworks_dragon.png',
    description: 'A dragon that creates spectacular fireworks',
    abilities: [
      {
        title: 'Fireworks Show',
        description: 'Creates beautiful fireworks that boost all nearby pets',
        category: 'special_abilities',
        pets: ['Fireworks Dragon']
      }
    ],
    base_weight: 20,
    hunger_rate: 6,
    xp_rate: 12,
    acquisition_method: 'New Year event (no longer available)',
    event_exclusive: true,
    limited_time: true
  },
  {
    id: 'golden_phoenix',
    name: 'Golden Phoenix',
    rarity: 'divine',
    obtainable: false,
    icon: '/pets/golden_phoenix.png',
    description: 'A legendary bird that rises from ashes',
    abilities: [
      {
        title: 'Rebirth',
        description: 'When this pet reaches max level, it can be reborn with enhanced abilities',
        category: 'special_abilities',
        pets: ['Golden Phoenix']
      }
    ],
    base_weight: 15,
    hunger_rate: 3,
    xp_rate: 8,
    acquisition_method: 'Special anniversary event (no longer available)',
    event_exclusive: true,
    limited_time: true
  }
]

const rarityColors = {
  common: 'from-gray-400 to-gray-500',
  uncommon: 'from-green-400 to-green-500',
  rare: 'from-blue-400 to-blue-500',
  legendary: 'from-purple-400 to-purple-500',
  mythical: 'from-orange-400 to-orange-500',
  divine: 'from-yellow-400 to-yellow-500',
  prismatic: 'from-pink-400 to-pink-500',
  unknown: 'from-gray-300 to-gray-400'
}

const rarityIcons = {
  common: <Star className="h-4 w-4" />,
  uncommon: <Star className="h-4 w-4" />,
  rare: <Star className="h-4 w-4" />,
  legendary: <Star className="h-4 w-4" />,
  mythical: <Star className="h-4 w-4" />,
  divine: <Star className="h-4 w-4" />,
  prismatic: <Star className="h-4 w-4" />,
  unknown: <Star className="h-4 w-4" />
}

const abilityCategoryIcons = {
  growth_harvest: <Leaf className="h-4 w-4" />,
  mutation_effects: <Zap className="h-4 w-4" />,
  experience_boosters: <TrendingUp className="h-4 w-4" />,
  egg_hatch: <Clock className="h-4 w-4" />,
  resource_gatherers: <Scale className="h-4 w-4" />,
  player_interaction: <Users className="h-4 w-4" />,
  special_abilities: <Shield className="h-4 w-4" />,
  pet_interactions: <Heart className="h-4 w-4" />
}

// Pet mechanics data
const petMechanics = {
  overview: {
    introduction: "Pets are a feature added to Grow a Garden in Update 1.04.0. Pets have unique abilities, and having multiple of the same pets can stack their abilities.",
    equipSlots: {
      base: 3,
      max: 8,
      upgradeMethod: "Players can get additional slots from buying gamepasses from the Limited Time Shop and/or trading aged pets in."
    },
    inventory: {
      base: 60,
      max: 85,
      upgradeMethod: "Players can increase it by buying gamepasses in the Limited Time Shop or trading aged pets in."
    },
    stats: "Pet Stats depend on their weight. As a pet grows older, its weight increases, making its abilities more efficient — such as reducing cooldowns or increasing success rates.",
    mutations: "Pets can be mutated by using the Mutation Machine or Pet Mutation Shards."
  },
  ageSystem: {
    description: "Age is a pet feature which increases over time through XP (in order to increase the Age, It must maintain its Hunger).",
    mechanics: [
      "As the pet gets older, the amount of XP required for the pet to age will increase",
      "It will also improve the pet's trait by either giving it a shorter cooldown, better chances, and other various buffs",
      "A pet cannot age if its hunger bar reaches 0 (Except for pets like starfish, whose passive is to gain XP)"
    ],
    viewing: "The player can view their pet's age by clicking on the pet and choosing the 'view' option, picking it up and stomping on it through their inventory, or pressing the << button on the right side of their screen and viewing the pet menu."
  },
  weightSystem: {
    description: "Pet Weight is a core feature that increases over time with age. For most pets, weights determine how strong their abilities are expressed such as reduced cooldowns or increased success rates.",
    classifications: {
      "Small": "W < 1 kg",
      "Normal": "1 kg ≤ W < 5 kg", 
      "Huge": "5 kg ≤ W < 7 kg",
      "Titanic": "8 kg ≤ W < 9 kg",
      "Godly": "9 kg ≤ W"
    },
    formula: "W₂ = (W₁ / (A₁ + 10)) × (A₂ + 10)",
    explanation: "Every time a pet ages it will gain an 11th of its starting weight. This means a pet at age 100 will be 10× the weight it was at age 1."
  },
  hungerSystem: {
    description: "Hunger is a pet feature which is required in order for the pet to grow older. When the hunger bar reaches 0, the pet can no longer grow.",
    feeding: "To feed a pet, simply equip any crop from the player's inventory and move closer to the player's pet.",
    methods: [
      "Click the pet and press the 'Feed' option",
      "Hold the interact button that appears once the player gets closer",
      "Feed through the pet menu by equipping the desired crop and selecting feed"
    ],
    effects: "The mutation and type of fruit depends on how much a pet's hunger has been restored. Each pet can have different hunger bars, which can depend on their rarity."
  },
  mutations: {
    description: "Pet Mutations are a core feature that were added in Update 1.14.0. They allow players to mutate pets of a level higher than 50 to gaining a permanent effect.",
    requirements: "Pets must be level 50 or higher to be mutated",
    method: "Use the Mutation Machine or Pet Mutation Shards"
  },
  equipmentUpgrades: [
    { slots: 1, ageRequirement: 20, robuxCost: 249 },
    { slots: 2, ageRequirement: 30, robuxCost: 399 },
    { slots: 3, ageRequirement: 45, robuxCost: 799 },
    { slots: 4, ageRequirement: 60, robuxCost: 1699 },
    { slots: 5, ageRequirement: 75, robuxCost: 1699 }
  ]
}

// XP Requirements table (first 30 levels for display)
const xpRequirements = [
  { age: 1, xpRequired: 20, accumulated: 20 },
  { age: 2, xpRequired: 81, accumulated: 101 },
  { age: 3, xpRequired: 184, accumulated: 285 },
  { age: 4, xpRequired: 329, accumulated: 614 },
  { age: 5, xpRequired: 516, accumulated: 1130 },
  { age: 6, xpRequired: 746, accumulated: 1876 },
  { age: 7, xpRequired: 1019, accumulated: 2895 },
  { age: 8, xpRequired: 1334, accumulated: 4229 },
  { age: 9, xpRequired: 1693, accumulated: 5922 },
  { age: 10, xpRequired: 2094, accumulated: 8016 },
  { age: 15, xpRequired: 4750, accumulated: 26022 },
  { age: 20, xpRequired: 8494, accumulated: 60569 },
  { age: 25, xpRequired: 13331, accumulated: 117112 },
  { age: 30, xpRequired: 19267, accumulated: 201135 },
  { age: 50, xpRequired: 54069, accumulated: 922398 },
  { age: 75, xpRequired: 122646, accumulated: 3107440 },
  { age: 100, xpRequired: 230343, accumulated: 7458160 }
]

// Pet mutations data
const petMutations = [
  { name: "Shiny", odds: 31.21, effect: "Provides an additional 15% boost to the pet's xp per second." },
  { name: "Inverted", odds: 15.11, effect: "Provides an additional 30% boost to the pet's xp per second." },
  { name: "Windy", odds: 9.06, effect: "Every 5-30m, 20-30% chance a nearby fruit becomes Windstruck!" },
  { name: "Frozen", odds: 9.06, effect: "Every 4:55, 20.05% chance a nearby fruit becomes Frozen" },
  { name: "Golden", odds: 6.04, effect: "Provides a big increase to the pet's passive." },
  { name: "Tiny", odds: 6.04, effect: "Pet is very tiny! Has 20% slower hunger rate. Gains 5-30 XP per second." },
  { name: "Mega", odds: 6.04, effect: "Pet is gigantic! Has 20% faster hunger rate. Gains 10-40 XP per second." },
  { name: "Tranquil", odds: 3.02, effect: "Every ~ 25 minutes, a nearby fruit finds inner peace, applying the Tranquil mutation!" },
  { name: "Corrupted", odds: 3.02, effect: "Every ~ 25 minutes, a nearby fruit embraces the corruption, applying the Corrupted mutation!" },
  { name: "IronSkin", odds: 3.02, effect: "35-45% chance to recover stolen fruit!" },
  { name: "Radiant", odds: 3.02, effect: "Every 30-20m, emits sunshine to advance a plant's growth by 24 hours!" },
  { name: "Shocked", odds: 3.02, effect: "During a Thunderstorm: Every 60-45s, has a 25-30% chance to attract lightning, shocking nearby fruits!" },
  { name: "Rainbow", odds: 3.02, effect: "Provides a bigger increase to the pet's passive!" },
  { name: "Ascended", odds: 0.3, effect: "Every 360-300m, applies Dawnbound mutation to a fruit!" }
]

// Overview Section Component
function OverviewSection({ mechanics }: { mechanics: any }) {
  return (
    <div className="space-y-8">
      {/* Introduction */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center space-x-2">
          <Info className="h-6 w-6 text-primary" />
          <span>Pet System Overview</span>
        </h2>
        <p className="text-muted-foreground mb-4">{mechanics.overview.introduction}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Equip Slots */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Package className="h-5 w-5 text-blue-500" />
              <span>Equip Slots</span>
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base Slots:</span>
                <span className="font-medium">{mechanics.overview.equipSlots.base}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Slots:</span>
                <span className="font-medium">{mechanics.overview.equipSlots.max}</span>
              </div>
              <p className="text-sm text-muted-foreground">{mechanics.overview.equipSlots.upgradeMethod}</p>
            </div>
          </div>

          {/* Inventory */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Users2 className="h-5 w-5 text-green-500" />
              <span>Inventory</span>
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base Limit:</span>
                <span className="font-medium">{mechanics.overview.inventory.base}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Limit:</span>
                <span className="font-medium">{mechanics.overview.inventory.max}</span>
              </div>
              <p className="text-sm text-muted-foreground">{mechanics.overview.inventory.upgradeMethod}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
          <h3 className="text-lg font-semibold text-foreground mb-2">Key Features</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start space-x-2">
              <Star className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
              <span>{mechanics.overview.stats}</span>
            </li>
            <li className="flex items-start space-x-2">
              <Zap className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
              <span>{mechanics.overview.mutations}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-card p-6 text-center">
          <div className="text-3xl font-bold text-primary mb-2">100+</div>
          <div className="text-sm text-muted-foreground">Total Pets</div>
        </div>
        <div className="glass-card p-6 text-center">
          <div className="text-3xl font-bold text-green-500 mb-2">8</div>
          <div className="text-sm text-muted-foreground">Rarity Tiers</div>
        </div>
        <div className="glass-card p-6 text-center">
          <div className="text-3xl font-bold text-purple-500 mb-2">8</div>
          <div className="text-sm text-muted-foreground">Ability Categories</div>
        </div>
      </div>
    </div>
  )
}

// Pets Section Component
function PetsSection({ 
  pets, 
  filteredPets, 
  searchTerm, 
  setSearchTerm, 
  selectedRarity, 
  setSelectedRarity, 
  selectedCategory, 
  setSelectedCategory, 
  obtainableOnly, 
  setObtainableOnly, 
  showFilters, 
  setShowFilters, 
  selectedPet, 
  setSelectedPet 
}: any) {
  // Separate obtainable and unobtainable pets
  const obtainablePets = filteredPets.filter(pet => pet.obtainable)
  const unobtainablePets = filteredPets.filter(pet => !pet.obtainable)

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search pets by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-modern"
          />
        </div>

        {/* Filters - Always Visible */}
        <div className="glass-card p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Rarity Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Rarity</label>
              <select
                value={selectedRarity}
                onChange={(e) => setSelectedRarity(e.target.value as PetRarity | 'all')}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-modern"
              >
                <option value="all">All Rarities</option>
                <option value="common">Common</option>
                <option value="uncommon">Uncommon</option>
                <option value="rare">Rare</option>
                <option value="legendary">Legendary</option>
                <option value="mythical">Mythical</option>
                <option value="divine">Divine</option>
                <option value="prismatic">Prismatic</option>
              </select>
            </div>

            {/* Ability Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Ability Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as PetAbilityCategory | 'all')}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-modern"
              >
                <option value="all">All Categories</option>
                <option value="growth_harvest">Growth & Harvest</option>
                <option value="mutation_effects">Mutation Effects</option>
                <option value="experience_boosters">Experience Boosters</option>
                <option value="egg_hatch">Egg & Hatch</option>
                <option value="resource_gatherers">Resource Gatherers</option>
                <option value="player_interaction">Player Interaction</option>
                <option value="special_abilities">Special Abilities</option>
                <option value="pet_interactions">Pet Interactions</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground">
          Showing {filteredPets.length} of {pets.length} pets
        </div>
      </div>

                {/* Obtainable Pets Section */}
          {obtainablePets.length > 0 && (
            <div className="space-y-4">
              <div className="pet-section-header">
                <div className="pet-section-indicator obtainable"></div>
                <h2 className="text-2xl font-bold text-foreground">Obtainable Pets</h2>
                <span className="pet-section-count obtainable">
                  {obtainablePets.length} pets
                </span>
              </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {obtainablePets.map((pet: Pet, index: number) => (
                <motion.div
                  key={pet.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPet(pet)}
                  className="glass-card p-6 pet-card obtainable"
                >
                  {/* Pet Image */}
                  <div className="pet-image-container">
                    <div className="pet-image-placeholder">
                      <Heart className="h-10 w-10 text-primary" />
                    </div>
                    
                    {/* Rarity Badge */}
                    <div className={`pet-rarity-badge pet-rarity-${pet.rarity} pet-badge-top-right`}>
                      {rarityIcons[pet.rarity]}
                      <span className="capitalize">{pet.rarity}</span>
                    </div>

                    {/* Obtainable Badge */}
                    <div className="pet-status-badge pet-status-obtainable pet-badge-top-left">
                      Obtainable
                    </div>
                  </div>

                  {/* Pet Info */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground text-lg">{pet.name}</h3>
                    
                    {pet.description && (
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {pet.description}
                      </p>
                    )}

                    {/* Abilities Preview */}
                    <div className="space-y-1">
                      {pet.abilities.slice(0, 2).map((ability: any, idx: number) => (
                        <div key={idx} className="flex items-center space-x-2 text-xs text-muted-foreground">
                          {abilityCategoryIcons[ability.category]}
                          <span className="truncate">{ability.title}</span>
                        </div>
                      ))}
                      {pet.abilities.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{pet.abilities.length - 2} more abilities
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

                {/* Unobtainable Pets Section */}
          {unobtainablePets.length > 0 && (
            <div className="space-y-4">
              <div className="pet-section-header">
                <div className="pet-section-indicator unobtainable"></div>
                <h2 className="text-2xl font-bold text-foreground">Unobtainable Pets</h2>
                <span className="pet-section-count unobtainable">
                  {unobtainablePets.length} pets
                </span>
              </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {unobtainablePets.map((pet: Pet, index: number) => (
                <motion.div
                  key={pet.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPet(pet)}
                  className="glass-card p-6 pet-card unobtainable"
                >
                  {/* Pet Image */}
                  <div className="pet-image-container">
                    <div className="pet-image-placeholder">
                      <Heart className="h-10 w-10 text-primary" />
                    </div>
                    
                    {/* Rarity Badge */}
                    <div className={`pet-rarity-badge pet-rarity-${pet.rarity} pet-badge-top-right`}>
                      {rarityIcons[pet.rarity]}
                      <span className="capitalize">{pet.rarity}</span>
                    </div>

                    {/* Unobtainable Badge */}
                    <div className="pet-status-badge pet-status-unobtainable pet-badge-top-left">
                      Unobtainable
                    </div>
                  </div>

                  {/* Pet Info */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground text-lg">{pet.name}</h3>
                    
                    {pet.description && (
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {pet.description}
                      </p>
                    )}

                    {/* Abilities Preview */}
                    <div className="space-y-1">
                      {pet.abilities.slice(0, 2).map((ability: any, idx: number) => (
                        <div key={idx} className="flex items-center space-x-2 text-xs text-muted-foreground">
                          {abilityCategoryIcons[ability.category]}
                          <span className="truncate">{ability.title}</span>
                        </div>
                      ))}
                      {pet.abilities.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{pet.abilities.length - 2} more abilities
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {filteredPets.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No pets found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  )
}

// Mechanics Section Component
function MechanicsSection({ mechanics, xpRequirements }: { mechanics: any, xpRequirements: any[] }) {
  return (
    <div className="space-y-8">
      {/* Age System */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center space-x-2">
          <Clock className="h-6 w-6 text-blue-500" />
          <span>Age System</span>
        </h2>
        <p className="text-muted-foreground mb-4">{mechanics.ageSystem.description}</p>
        
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Key Mechanics</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {mechanics.ageSystem.mechanics.map((mechanic: string, idx: number) => (
              <li key={idx} className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                <span>{mechanic}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
          <h3 className="text-lg font-semibold text-foreground mb-2">Viewing Pet Age</h3>
          <p className="text-sm text-muted-foreground">{mechanics.ageSystem.viewing}</p>
        </div>
      </div>

      {/* Weight System */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center space-x-2">
          <Scale className="h-6 w-6 text-green-500" />
          <span>Weight System</span>
        </h2>
        <p className="text-muted-foreground mb-4">{mechanics.weightSystem.description}</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Classifications */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Weight Classifications</h3>
            <div className="space-y-2">
              {Object.entries(mechanics.weightSystem.classifications).map(([name, range]) => (
                <div key={name} className="flex justify-between items-center p-2 bg-muted rounded-lg">
                  <span className="font-medium text-foreground">{name}</span>
                  <span className="text-sm text-muted-foreground">{range as string}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Formula */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3">Weight Formula</h3>
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-sm font-mono text-foreground mb-2">{mechanics.weightSystem.formula}</p>
              <p className="text-xs text-muted-foreground">{mechanics.weightSystem.explanation}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hunger System */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center space-x-2">
          <Droplets className="h-6 w-6 text-orange-500" />
          <span>Hunger & Feeding</span>
        </h2>
        <p className="text-muted-foreground mb-4">{mechanics.hungerSystem.description}</p>
        
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">How to Feed</h3>
            <p className="text-sm text-muted-foreground mb-3">{mechanics.hungerSystem.feeding}</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {mechanics.hungerSystem.methods.map((method: string, idx: number) => (
                <li key={idx} className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  <span>{method}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
            <h3 className="text-lg font-semibold text-foreground mb-2">Effects</h3>
            <p className="text-sm text-muted-foreground">{mechanics.hungerSystem.effects}</p>
          </div>
        </div>
      </div>

      {/* XP Requirements Table */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center space-x-2">
          <Calculator className="h-6 w-6 text-purple-500" />
          <span>XP Requirements</span>
        </h2>
        <p className="text-muted-foreground mb-4">
          The following table shows the XP requirement for a pet to age at different ages.
        </p>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2 font-semibold">Age</th>
                <th className="text-left p-2 font-semibold">XP Required</th>
                <th className="text-left p-2 font-semibold">Accumulated XP</th>
              </tr>
            </thead>
            <tbody>
              {xpRequirements.map((req) => (
                <tr key={req.age} className="border-b border-border/50 hover:bg-muted/50">
                  <td className="p-2 font-medium">{req.age}</td>
                  <td className="p-2">{req.xpRequired.toLocaleString()}</td>
                  <td className="p-2">{req.accumulated.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Mutations Section Component
function MutationsSection({ mutations }: { mutations: any[] }) {
  return (
    <div className="space-y-8">
      {/* Introduction */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center space-x-2">
          <Zap className="h-6 w-6 text-yellow-500" />
          <span>Pet Mutations</span>
        </h2>
        <p className="text-muted-foreground mb-4">
          Pet Mutations are a core feature that were added in Update 1.14.0. They allow players to mutate pets of a level higher than 50 to gaining a permanent effect.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
            <h3 className="text-lg font-semibold text-foreground mb-2">Requirements</h3>
            <p className="text-sm text-muted-foreground">Pets must be level 50 or higher to be mutated</p>
          </div>
          <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
            <h3 className="text-lg font-semibold text-foreground mb-2">Method</h3>
            <p className="text-sm text-muted-foreground">Use the Mutation Machine or Pet Mutation Shards</p>
          </div>
        </div>
      </div>

      {/* Mutations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mutations.map((mutation, idx) => (
          <motion.div
            key={mutation.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-foreground">{mutation.name}</h3>
              <div className="px-2 py-1 bg-primary/20 rounded text-xs font-medium text-primary">
                {mutation.odds}%
              </div>
            </div>
            <p className="text-sm text-muted-foreground">{mutation.effect}</p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

export default function PetWiki() {
  const { fetchPetWiki } = useApi()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRarity, setSelectedRarity] = useState<PetRarity | 'all'>('all')
  const [selectedCategory, setSelectedCategory] = useState<PetAbilityCategory | 'all'>('all')
  const [obtainableOnly, setObtainableOnly] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'pets' | 'mechanics' | 'mutations'>('overview')

  // Use static data for now since API might not be ready
  const pets = staticPetData

  const filteredPets = useMemo(() => {
    return pets.filter(pet => {
      const matchesSearch = pet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           pet.description?.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesRarity = selectedRarity === 'all' || pet.rarity === selectedRarity
      
      const matchesCategory = selectedCategory === 'all' || 
                             pet.abilities.some(ability => ability.category === selectedCategory)
      
      return matchesSearch && matchesRarity && matchesCategory
    })
  }, [pets, searchTerm, selectedRarity, selectedCategory, obtainableOnly])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Pet Wiki</h1>
          <p className="text-muted-foreground mt-2">
            Complete guide to {pets.length} pets and their mechanics
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border">
        {[
          { id: 'overview', label: 'Overview', icon: Info },
          { id: 'pets', label: 'Pets', icon: Heart },
          { id: 'mechanics', label: 'Mechanics', icon: Settings },
          { id: 'mutations', label: 'Mutations', icon: Zap }
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg transition-modern ${
              activeTab === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <OverviewSection mechanics={petMechanics} />
          </motion.div>
        )}

        {activeTab === 'pets' && (
          <motion.div
            key="pets"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <PetsSection 
              pets={pets}
              filteredPets={filteredPets}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedRarity={selectedRarity}
              setSelectedRarity={setSelectedRarity}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              obtainableOnly={obtainableOnly}
              setObtainableOnly={setObtainableOnly}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              selectedPet={selectedPet}
              setSelectedPet={setSelectedPet}
            />
          </motion.div>
        )}

        {activeTab === 'mechanics' && (
          <motion.div
            key="mechanics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <MechanicsSection mechanics={petMechanics} xpRequirements={xpRequirements} />
          </motion.div>
        )}

        {activeTab === 'mutations' && (
          <motion.div
            key="mutations"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <MutationsSection mutations={petMutations} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pet Details Modal */}
      <AnimatePresence>
        {selectedPet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedPet(null)} />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto glass-card p-8"
            >
              <PetDetailsModal pet={selectedPet} onClose={() => setSelectedPet(null)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Pet Details Modal Component
function PetDetailsModal({ pet, onClose }: { pet: Pet; onClose: () => void }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">{pet.name}</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-lg transition-modern"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Pet Image and Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pet Image */}
        <div className="relative">
          <div className="w-full h-64 bg-gradient-to-br from-muted to-muted/50 rounded-xl flex items-center justify-center">
            <div className="w-32 h-32 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
              <Heart className="h-16 w-16 text-primary" />
            </div>
          </div>
          
          {/* Rarity Badge */}
          <div className={`pet-rarity-badge large pet-rarity-${pet.rarity} pet-badge-large-top-right`}>
            {rarityIcons[pet.rarity]}
            <span className="capitalize">{pet.rarity}</span>
          </div>

          {/* Status Badge */}
          <div className={`pet-status-badge large ${pet.obtainable ? 'pet-status-obtainable' : 'pet-status-unobtainable'} pet-badge-large-top-left`}>
            {pet.obtainable ? 'Obtainable' : 'Unobtainable'}
          </div>
        </div>

        {/* Basic Info */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Description</h3>
            <p className="text-muted-foreground">
              {pet.description || "No description available."}
            </p>
          </div>

          {pet.category && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Category</h3>
              <p className="text-muted-foreground capitalize">{pet.category}</p>
            </div>
          )}

          {pet.acquisition_method && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Acquisition</h3>
              <p className="text-muted-foreground">{pet.acquisition_method}</p>
            </div>
          )}

          {pet.optimal_conditions && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Optimal Conditions</h3>
              <p className="text-muted-foreground">{pet.optimal_conditions}</p>
            </div>
          )}

          {pet.evolution_path && pet.evolution_path.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Evolution Path</h3>
              <div className="flex flex-wrap gap-2">
                {pet.evolution_path.map((evolution, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primary/20 rounded text-sm text-primary">
                    {evolution}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      {(pet.base_weight || pet.hunger_rate || pet.xp_rate) && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Stats</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pet.base_weight && (
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{pet.base_weight}kg</div>
                <div className="text-sm text-muted-foreground">Base Weight</div>
              </div>
            )}
            {pet.hunger_rate !== undefined && (
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-500">{pet.hunger_rate}</div>
                <div className="text-sm text-muted-foreground">Hunger Rate</div>
              </div>
            )}
            {pet.xp_rate && (
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">{pet.xp_rate}</div>
                <div className="text-sm text-muted-foreground">XP Rate</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Abilities */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Abilities</h3>
        <div className="space-y-4">
          {pet.abilities.map((ability, idx) => (
            <div key={idx} className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                {abilityCategoryIcons[ability.category]}
                <h4 className="font-semibold text-foreground">{ability.title}</h4>
              </div>
              <p className="text-muted-foreground text-sm">{ability.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
} 